import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';

import i18n from 'i18n-js';
import { Form, Formik, FieldArray, getIn } from 'formik';
import Dialog from '@material-ui/core/Dialog';
import * as yup from 'yup';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import { DialogActions } from '@material-ui/core';

import { Input } from 'components/shared';
import { HourData } from 'components/restaurantInformation/components/types';
import ImageContainer, {
    ImageContainerRefProps,
} from 'components/shared/components/ImageContainer';
import { regexHelper } from 'helpers/regexHelper';
import { useAppSelector } from 'index';
import { useStyles } from '../styles';
import { SectionData, AdditionalPrice } from 'store/menuDetailsStore/reducer';
import menuDetailsStore from 'store/menuDetailsStore';
import * as FormikFields from 'formik-material-ui';
import { MenuType } from 'constants/enums';

interface AddDailyMenuSectionProps {
    section?: SectionData;
    isOpen: boolean;
    onClose: () => void;
    menuType: MenuType;
    //onAdd: (model: any) => void;
    //menus: string[];
}

export interface DaysWithHours {
    sunday: HourData;
    monday: HourData;
    tuesday: HourData;
    wednesday: HourData;
    thursday: HourData;
    friday: HourData;
    saturday: HourData;
}

export default function DailyMenuSectionManagementModal(props: AddDailyMenuSectionProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const menu = useAppSelector((state) => state.menuDetails.menu);
    const imageContainerRef = useRef<ImageContainerRefProps>(null);
    const menus = !!props.section?.id
        ? menu.sections.filter((x) => x.id !== props.section!.id).map((y) => y.name.toUpperCase())
        : menu.sections.map((x) => x.name.toUpperCase());

    function prepareTitle() {
        if (!!props.section?.id) {
            return props.menuType === MenuType.Daily
                ? i18n.t('common.editMenu')
                : i18n.t('common.editCourse');
        } else {
            return props.menuType === MenuType.Daily
                ? i18n.t('restaurant.addNewMenu')
                : i18n.t('common.newCourse');
        }
    }
    function saveChanges(
        name: string,
        price: number,
        photo: string,
        priceLevels: AdditionalPrice[],
        isCustomPrice: boolean
    ) {
        if (props.section?.id) {
            dispatch(
                menuDetailsStore.actionCreators.editDailyMenuSection(props.section?.id, {
                    name,
                    price,
                    photo,
                    priceLevels,
                    isCustomPrice,
                })
            );
        } else {
            dispatch(
                menuDetailsStore.actionCreators.addSection(
                    props.menuType === MenuType.Daily
                        ? {
                              name,
                              price,
                              photo,
                              priceLevels,
                              isCustomPrice,
                              menuId: menu.id,
                          }
                        : { name, price, photo, menuId: menu.id }
                )
            );
        }
        props.onClose();
    }

    const validationSchema = yup.object().shape({
        name: yup
            .string()
            .required(
                i18n.t('form.errors.required', {
                    name: 'Name',
                })
            )
            .test(
                'existsCheck',
                i18n.t('form.errors.alreadyTaken'),
                (value: any) => !!!props.section?.id || !menus.includes(value?.toUpperCase())
            ),
        price: yup
            .number()
            .required(
                i18n.t('form.errors.required', {
                    name: 'Price',
                })
            )
            .typeError(i18n.t('form.errors.onlyDigits'))
            .min(0, i18n.t('form.errors.greaterZero')),
        priceLevels: yup.array().of(
            yup.object().shape({
                price: yup
                    .number()
                    .typeError(i18n.t('form.errors.onlyDigits'))
                    .min(0, i18n.t('form.errors.greaterZero'))
                    .test(
                        'is-decimal',
                        i18n.t('form.errors.twoDigitsAfterComma'),
                        (val?: number) => {
                            if (val) {
                                return regexHelper.twoDigisAfterComma.test(val.toString());
                            }
                            return true;
                        }
                    ),
            })
        ),
    });

    function preparePricelevels() {
        let priceLevels: {
            price: string;
            priceLevelId: string;
        }[] = [];
        if (!!props.section?.priceLevels?.length) {
            props.section?.priceLevels.forEach((pl) =>
                priceLevels.push({ ...pl, price: pl.price.toFixed(2) })
            );
        }
        restaurant.pricingLevels.forEach((pl) => {
            if (priceLevels.every((l) => l.priceLevelId !== pl.id)) {
                priceLevels.push({
                    priceLevelId: pl.id,
                    price: props.section?.price?.toFixed(2) ?? '0.00',
                });
            }
        });

        return priceLevels;
    }

    const initialValues = {
        name: props.section?.name ?? '',
        price: props.section?.price?.toFixed(2) ?? '0.00',
        photo: props.section?.photo ?? '',
        priceLevels: preparePricelevels(),
        isCustomPrice: props.section?.isCustomPrice ?? false,
    };

    return (
        <Dialog
            open={props.isOpen}
            aria-labelledby="form-dialog-title"
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <DialogTitle id="form-dialog-title">{prepareTitle()}</DialogTitle>
            <DialogContent>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        imageContainerRef?.current?.uploadImage().then((photo) => {
                            if (photo.length > 0) values.photo = photo;
                            saveChanges(
                                values.name,
                                +values.price,
                                values.photo,
                                values.priceLevels.map((pl) => ({
                                    ...pl,
                                    price:
                                        values.price !== initialValues.price
                                            ? +values.price
                                            : +pl.price,
                                })),
                                values.isCustomPrice
                            );
                        });
                    }}
                >
                    {({ errors, submitForm, values }) => (
                        <Form>
                            <ImageContainer ref={imageContainerRef} photo={values.photo} />
                            <>
                                <Input
                                    error={errors.name}
                                    variant="filled"
                                    fullWidth={true}
                                    name={`name`}
                                    type="text"
                                    label={
                                        props.menuType === MenuType.Daily
                                            ? i18n.t('form.menuName')
                                            : i18n.t('common.courseName')
                                    }
                                    placeholder={i18n.t('form.name')}
                                />
                                <br />
                                <Input
                                    error={errors.price}
                                    disabled={values.isCustomPrice}
                                    variant="filled"
                                    fullWidth={true}
                                    name={`price`}
                                    type="text"
                                    label={i18n.t('form.price')}
                                    placeholder={i18n.t('form.price')}
                                />
                                <div style={{ display: 'flex' }}>
                                    <FieldArray
                                        name="priceLevels"
                                        render={() => (
                                            <>
                                                {values.priceLevels.map((pl, index) => {
                                                    const priceName = `priceLevels[${index}].price`;
                                                    const priceError = getIn(errors, priceName);
                                                    const label =
                                                        restaurant.pricingLevels.find(
                                                            (level) => level.id === pl.priceLevelId
                                                        )?.name || 'additional price';
                                                    return (
                                                        <div
                                                            className={classes.priceLevel}
                                                            key={index}
                                                        >
                                                            <Input
                                                                error={priceError}
                                                                disabled={values.isCustomPrice}
                                                                variant="filled"
                                                                name={priceName}
                                                                type="text"
                                                                label={label}
                                                                placeholder={label}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        )}
                                    />
                                </div>
                                {props.menuType === MenuType.Daily && (
                                    <Input
                                        component={FormikFields.CheckboxWithLabel}
                                        variant="filled"
                                        name="isCustomPrice"
                                        type="checkbox"
                                        Label={{
                                            label: i18n.t('common.customPrice'),
                                        }}
                                        placeholder={i18n.t('common.customPrice')}
                                        inputProps={{
                                            'aria-label': 'secondary checkbox',
                                        }}
                                        color="primary"
                                    />
                                )}
                            </>
                            <br />
                            <DialogActions>
                                <Button type="button" color="primary" onClick={props.onClose}>
                                    {i18n.t('button.cancel')}
                                </Button>
                                <Button onClick={submitForm} variant="contained" color="primary">
                                    {props.section?.id
                                        ? i18n.t('common.save')
                                        : i18n.t('common.add')}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
}
